import { useState } from "react";
import { importApi, type ImportReport } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad, errText } from "../ui/useLoad";
import { ErrorNote, PageHead } from "../ui/bits";
import { readTextAuto } from "../ui/download";
import { SheetImport } from "./SheetImport";

// a problem from the server, said with what it is about ("Ledger "Office rent": …"), without the database's wording
const KIND: Record<string, string> = { ledger: "Account", group: "Group", voucher: "Entry", party: "Party", item: "Item", unit: "Unit", category: "Category",
  location: "Warehouse", stock: "Opening stock", bill: "Bill" };
const plain = (m: string) => m.replace(/^books:\s*/, "")
  .replace(/duplicate key value violates unique constraint "(\w+?)_(code|name)\w*"/, (_, t: string, f: string) => `another ${t.replace(/s$/, "").replace(/_/g, " ")} already has this ${f}`)
  .replace(/violates (check|not-null|foreign key) constraint.*/, "has a value Books can't take");
const say = (x: unknown) => {
  if (typeof x === "string") return plain(x);
  if (!x || typeof x !== "object") return String(x);
  const o = x as Record<string, unknown>;
  const m = plain(String(o.message ?? JSON.stringify(x)));
  const what = o.number ? `${KIND.voucher} ${String(o.number)}${o.date ? ` of ${String(o.date)}` : ""}` : o.key && o.kind && KIND[String(o.kind)] ? `${KIND[String(o.kind)]} “${String(o.key).replace(/^\w+:/, "")}”` : "";
  return what && !m.includes(String(o.key ?? "\u0000").replace(/^\w+:/, "")) ? `${what}: ${m}` : m;
};

// Company › Import: bring books in from Tally (XML export) or Zoho Books (API bundle). Check first (nothing is kept),
// then import; every voucher posts through the normal rules and the balances are tied out to the source.
export function Import() {
  const { company } = useCompany();
  const [kind, setKind] = useState<"sheet" | "tally" | "zoho" | "canonical">("sheet");
  const [files, setFiles] = useState<{ masters?: File; daybook?: File; bundle?: File }>({});
  const [asOn, setAsOn] = useState("");
  const [stock, setStock] = useState("opening");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [rev, setRev] = useState(0);
  const history = useLoad(() => importApi.history(company.id), [company.id, rev]);

  const run = async (commit: boolean) => {
    setBusy(true); setErr(null); if (!commit) setReport(null);
    try {
      let payload: Record<string, unknown>;
      if (kind === "tally") {
        if (!files.masters) throw new Error("Choose the Tally masters export (XML).");
        payload = { masters: await readTextAuto(files.masters), daybook: files.daybook ? await readTextAuto(files.daybook) : null };
      } else {
        const f = files.bundle;
        if (!f) throw new Error("Choose the file.");
        const j = JSON.parse(await readTextAuto(f));
        payload = kind === "zoho" ? { bundle: j } : { data: j };
      }
      const r = await importApi.run(company.id, kind, payload, kind === "tally" ? { ...(asOn ? { as_on: asOn } : {}), stock } : {}, commit);
      setReport(r);
      if (commit) setRev(rev + 1);
    } catch (x) { setErr(x instanceof SyntaxError ? "That file isn't JSON." : errText(x)); } finally { setBusy(false); }
  };
  const clean = report && report.problems.length === 0;
  return (
    <section className="page settings">
      <PageHead crumb="Company" title="Bring books from another system" />
      <section className="card panel settings-box">
        <div className="fields inline">
          <label>From<select value={kind} onChange={(e) => { setKind(e.target.value as typeof kind); setReport(null); }}>
            <option value="sheet">Excel or CSV (any system)</option><option value="tally">Tally (XML export)</option><option value="zoho">Zoho Books (API bundle)</option><option value="canonical">Altekore canonical JSON</option></select></label>
        </div>
        {kind === "sheet" ? <SheetImport onDone={() => setRev(rev + 1)} Report={Report} /> : kind === "tally" ? <>
          <p className="muted small">In Tally: export the masters (Gateway › Display › List of Accounts, with inventory masters) and the Day Book for the period, both as XML.
            Add the trial balance date to tie the closing balances out.</p>
          <div className="fields two">
            <label className="file">Tally masters export (XML)<input type="file" accept=".xml,text/xml" onChange={(e) => setFiles({ ...files, masters: e.target.files?.[0] })} /></label>
            <label className="file">Tally day book export (XML, optional)<input type="file" accept=".xml,text/xml" onChange={(e) => setFiles({ ...files, daybook: e.target.files?.[0] })} /></label>
            <label>Balances as at<input type="date" value={asOn} onChange={(e) => setAsOn(e.target.value)} /></label>
            <label>Stock<select value={stock} onChange={(e) => setStock(e.target.value)}><option value="opening">Opening stock from Tally</option>
              <option value="closing">Closing stock at the cut-over</option><option value="none">No stock (accounts only)</option></select></label>
          </div>
        </> : <label className="file">{kind === "zoho" ? "Zoho Books bundle (JSON)" : "Canonical JSON"}<input type="file" accept=".json,application/json" onChange={(e) => setFiles({ ...files, bundle: e.target.files?.[0] })} /></label>}
        {kind !== "sheet" && <div className="row"><button className="primary" disabled={busy} onClick={() => void run(false)}>{busy ? "Working…" : "Check"}</button>
          {clean && !report!.committed && <button className="primary" disabled={busy} onClick={() => void run(true)}>Import now</button>}</div>}
        <ErrorNote text={err} />
      </section>
      {report && kind !== "sheet" && <Report r={report} />}
      <section className="card panel settings-box"><h2>Earlier imports</h2>
        {!history.data?.length ? <p className="muted small">None yet.</p> :
          <table className="grid-table compact"><tbody>{history.data.map((h) => <tr key={h.id}><td>{new Date(h.imported_at).toLocaleString("en-IN")}</td><td>{h.source}</td>
            <td>{h.source_company ?? ""}</td><td className="muted small">{h.counts ? Object.entries(h.counts).filter(([, v]) => v).map(([k, v]) => `${k.replace(/_/g, " ")} ${v}`).join(" · ") : ""}</td></tr>)}</tbody></table>}
      </section>
    </section>
  );
}

function Report({ r }: { r: ImportReport }) {
  const diffs = r.tie_out?.differences ?? [];
  return (
    <section className="card panel settings-box">
      <h2>{r.committed ? "Imported" : r.problems.length ? "Problems to fix first" : "Ready to import"}</h2>
      <p className="muted small">{r.source_company ? `${r.source_company}: ` : ""}{r.found.groups} groups, {r.found.ledgers} ledgers, {r.found.vouchers} vouchers{r.found.items ? `, ${r.found.items} items` : ""} found.
        {r.committed ? "" : " Nothing has been kept yet."}</p>
      {r.problems.length > 0 && <ul className="msg err errors">{r.problems.map((p, i) => <li key={i}>{say(p)}</li>)}</ul>}
      {Object.keys(r.counts ?? {}).length > 0 && <p className="small">{Object.entries(r.counts).filter(([, v]) => v).map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ")}</p>}
      {r.tie_out && <p className={`small ${diffs.length ? "warn" : ""}`}>Tie-out: {r.tie_out.ledgers_compared ?? 0} ledgers compared{diffs.length ? `, ${diffs.length} differ` : ", all agree"}.</p>}
      {diffs.length > 0 && <ul className="small warn">{diffs.map((d, i) => <li key={i}>{say(d)}</li>)}</ul>}
      {r.opening_difference ? <p className="warn small">Difference in opening balances: {r.opening_difference}</p> : null}
      {r.notices?.length > 0 && <><h3>Notes</h3><ul className="small">{r.notices.map((n, i) => <li key={i}>{n.message ?? n.code.replace(/_/g, " ")}</li>)}</ul></>}
      {r.warnings?.length > 0 && <><h3>From the converter</h3><ul className="small muted">{r.warnings.map((w, i) => <li key={i}>{say(w)}</li>)}</ul></>}
    </section>
  );
}
